import { CubeQuery } from '@/types/cube';
import { Filter, Query } from '@cubejs-client/core';
import { DEFAULT_COUNTRY_MEMBER, getCountryDimensionForCube } from './countryFilterMapping';
import { UnsanitizedQuery } from './sanitizeCubeQuery';

export function extractCubeName(member: string): string {
  const dotIndex = member.indexOf('.');
  return dotIndex === -1 ? member : member.substring(0, dotIndex);
}

export function extractCubeNamesFromQuery(query: CubeQuery | UnsanitizedQuery): string[] {
  const cubeNames = new Set<string>();
  const typedQuery = query as Query;

  if (typedQuery.measures) {
    for (const measure of typedQuery.measures) {
      cubeNames.add(extractCubeName(measure));
    }
  }
  if (typedQuery.dimensions) {
    for (const dimension of typedQuery.dimensions) {
      cubeNames.add(extractCubeName(dimension));
    }
  }
  if (typedQuery.timeDimensions) {
    for (const timeDimension of typedQuery.timeDimensions) {
      if (timeDimension?.dimension) {
        cubeNames.add(extractCubeName(timeDimension.dimension));
      }
    }
  }

  return Array.from(cubeNames);
}

// Returns the predicates to AND into the parent; inside an or-group a multi-cube expansion keeps its own and.
function replaceCountryFilterMember(filter: Filter, targetMembers: readonly string[]): Filter[] {
  if ('member' in filter && filter.member === DEFAULT_COUNTRY_MEMBER) {
    return targetMembers.map(member => ({ ...filter, member }));
  }
  if ('and' in filter && Array.isArray(filter.and)) {
    const and = filter.and.flatMap((nested: Filter) => replaceCountryFilterMember(nested, targetMembers));
    return [{ ...filter, and }];
  }
  if ('or' in filter && Array.isArray(filter.or)) {
    const or = filter.or.map((nested: Filter) => {
      const predicates = replaceCountryFilterMember(nested, targetMembers);
      return predicates.length === 1 ? predicates[0] : { and: predicates };
    });
    return [{ ...filter, or }];
  }
  return [filter];
}

// One predicate per cube that owns a country column, ANDed, like Cube's getUserSpecificCountryFilter.
function getCountryTargetMembers(query: CubeQuery | UnsanitizedQuery): string[] {
  const targetMembers = new Set<string>();
  for (const cubeName of extractCubeNamesFromQuery(query)) {
    if (cubeName === 'hcp') continue;
    const member = getCountryDimensionForCube(cubeName);
    if (member !== DEFAULT_COUNTRY_MEMBER) targetMembers.add(member);
  }
  return Array.from(targetMembers);
}

export function resolveCountryFilters(
  query: CubeQuery,
  filters: Filter[],
  preserveDefaultCountryFilter?: boolean,
): CubeQuery;
export function resolveCountryFilters(
  query: UnsanitizedQuery,
  filters: Filter[],
  preserveDefaultCountryFilter?: boolean,
): UnsanitizedQuery;
// Last overload serves callers holding an un-narrowed union (sanitizeCubeQuery)
export function resolveCountryFilters(
  query: CubeQuery | UnsanitizedQuery,
  filters: Filter[],
  preserveDefaultCountryFilter?: boolean,
): CubeQuery | UnsanitizedQuery;
export function resolveCountryFilters(
  query: CubeQuery | UnsanitizedQuery,
  filters: Filter[],
  preserveDefaultCountryFilter = false,
): CubeQuery | UnsanitizedQuery {
  if (preserveDefaultCountryFilter || !filters.length) return query;

  const targetMembers = getCountryTargetMembers(query);
  if (!targetMembers.length) return query;
  return {
    ...query,
    filters: filters.flatMap(existingFilter => replaceCountryFilterMember(existingFilter, targetMembers)),
  };
}
